import { GroupBusinessReport } from '../../../service/incomeApi';

/**
 * 8个分析维度类型
 */
export enum DimensionType {
  REVENUE = 'revenue', // 营收维度
  OCCUPANCY = 'occupancy', // 入住率维度
  PRICE = 'price', // 价格维度
  CUSTOMER_SOURCE = 'customer_source', // 客源结构维度
  ROOM_STATUS = 'room_status', // 房态维度
  REVENUE_EFFICIENCY = 'revenue_efficiency', // 收入效率维度
  TIME = 'time', // 时间维度
  OVERALL_HEALTH = 'overall_health', // 综合健康度维度
}

/**
 * 检测结果状态
 */
export enum DetectionStatus {
  NORMAL = 'normal', // 正常
  WARNING = 'warning', // 警告
  ERROR = 'error', // 错误
  CRITICAL = 'critical', // 严重
}

/**
 * 维度分析结果
 */
export interface DimensionAnalysisResult {
  dimension: DimensionType;
  storeId: number;
  storeName: string;
  status: DetectionStatus;
  score: number; // 0-100分
  indicators: {
    name: string;
    value: number;
    expected?: number;
    deviation?: number;
    status: DetectionStatus;
  }[];
  message: string;
  suggestions?: string[];
  timestamp: Date;
}

/**
 * 检测结果
 */
export interface DetectionResult {
  itemId: string;
  itemName: string;
  dimension: DimensionType;
  storeId: number;
  storeName: string;
  status: DetectionStatus;
  message: string;
  indicators: {
    name: string;
    value: number;
    expected?: number;
    deviation?: number;
  }[];
  timestamp: Date;
  suggestions?: string[];
}

/**
 * 门店数据缓存
 */
export interface StoreDataCache {
  storeId: number;
  storeName: string;
  data: GroupBusinessReport;
  timestamp: Date;
  dimensions: {
    [key in DimensionType]?: DimensionAnalysisResult;
  };
}

/**
 * 数据缓存管理器
 */
class DataCacheManager {
  private cache: Map<number, StoreDataCache> = new Map();
  private maxAge: number = 2 * 60 * 1000; // 2分钟缓存有效期

  /**
   * 更新门店数据缓存
   */
  updateStoreData(storeId: number, storeName: string, data: GroupBusinessReport): void {
    this.cache.set(storeId, {
      storeId,
      storeName,
      data,
      timestamp: new Date(),
      dimensions: {},
    });
  }

  /**
   * 批量更新门店数据缓存
   */
  updateStoresData(storesData: Array<{ storeId: number; storeName: string; data: GroupBusinessReport }>): void {
    storesData.forEach(({ storeId, storeName, data }) => {
      this.updateStoreData(storeId, storeName, data);
    });
  }

  /**
   * 获取门店数据
   */
  getStoreData(storeId: number): StoreDataCache | undefined {
    const cached = this.cache.get(storeId);
    if (!cached) return undefined;

    // 检查缓存是否过期
    const age = Date.now() - cached.timestamp.getTime();
    if (age > this.maxAge) {
      this.cache.delete(storeId);
      return undefined;
    }

    return cached;
  }

  /**
   * 获取所有门店数据
   */
  getAllStoresData(): StoreDataCache[] {
    const now = Date.now();
    const validStores: StoreDataCache[] = [];

    this.cache.forEach((cached, storeId) => {
      const age = now - cached.timestamp.getTime();
      if (age <= this.maxAge) {
        validStores.push(cached);
      } else {
        this.cache.delete(storeId);
      }
    });

    return validStores;
  }

  /**
   * 更新门店的维度分析结果
   */
  updateDimensionAnalysis(storeId: number, dimension: DimensionType, result: DimensionAnalysisResult): void {
    const cached = this.cache.get(storeId);
    if (cached) {
      cached.dimensions[dimension] = result;
    }
  }

  /**
   * 清除缓存
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * 获取缓存统计
   */
  getStats(): { total: number; valid: number; expired: number } {
    const now = Date.now();
    let valid = 0;
    let expired = 0;

    this.cache.forEach((cached) => {
      const age = now - cached.timestamp.getTime();
      if (age <= this.maxAge) {
        valid++;
      } else {
        expired++;
      }
    });

    return {
      total: this.cache.size,
      valid,
      expired,
    };
  }
}

// 全局缓存管理器实例
export const dataCacheManager = new DataCacheManager();

/**
 * 生成基于数据和时间戳的随机数（确保每次调用都不同）
 */
function getSeededRandom(data: GroupBusinessReport, dimension: string): number {
  const timeSeed = Date.now();
  const dataSeed = data.hotelId + (data.allRevenue || 0) + (data.occupancyRate || 0);
  const dimensionSeed = dimension.charCodeAt(0) + dimension.length;
  const seed = timeSeed + dataSeed + dimensionSeed;
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * 维度1：营收维度分析（Mock版本 - 假分析）
 */
export function analyzeRevenueDimension(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): DimensionAnalysisResult {
  // 从mock数据中取数
  const allRevenue = data.allRevenue || 0;
  const roomRevenue = data.roomRevenue || 0;
  const membershipRevenue = data.membershipRevenue || 0;
  
  // 假分析：使用基于数据和时间戳的随机数，确保每次调用都不同
  const randomStatus = getSeededRandom(data, 'revenue');
  let status = DetectionStatus.NORMAL;
  let score = 100;
  
  // 调整概率：30%警告，10%错误，60%正常
  if (randomStatus < 0.1) {
    status = DetectionStatus.ERROR;
    score = 40 + getSeededRandom(data, 'revenue_error') * 20;
  } else if (randomStatus < 0.4) {
    status = DetectionStatus.WARNING;
    score = 60 + getSeededRandom(data, 'revenue_warning') * 20;
  } else {
    score = 75 + getSeededRandom(data, 'revenue_normal') * 25;
  }

  const indicators: DimensionAnalysisResult['indicators'] = [
    {
      name: '总收入',
      value: allRevenue,
      expected: allRevenue * 1.1,
      deviation: -10,
      status: status,
    },
    {
      name: '房间收入占比',
      value: allRevenue > 0 ? (roomRevenue / allRevenue) * 100 : 0,
      expected: 70,
      deviation: allRevenue > 0 ? (roomRevenue / allRevenue) * 100 - 70 : 0,
      status: status,
    },
    {
      name: '会员收入占比',
      value: allRevenue > 0 ? (membershipRevenue / allRevenue) * 100 : 0,
      expected: 20,
      deviation: allRevenue > 0 ? (membershipRevenue / allRevenue) * 100 - 20 : 0,
      status: status,
    },
  ];

  const messages = status !== DetectionStatus.NORMAL 
    ? ['营收数据存在异常，建议关注'] 
    : ['营收数据正常'];

  return {
    dimension: DimensionType.REVENUE,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status,
    score: Math.max(0, Math.min(100, score)),
    indicators,
    message: messages.join('；'),
    suggestions: status !== DetectionStatus.NORMAL ? ['建议分析收入下降原因'] : [],
    timestamp: new Date(),
  };
}

/**
 * 维度2：入住率维度分析（Mock版本 - 假分析）
 */
export function analyzeOccupancyDimension(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): DimensionAnalysisResult {
  // 从mock数据中取数
  const occupancyRate = data.occupancyRate || 0;
  const roomTotal = data.roomTotal || 0;
  const roomAvaliable = data.roomAvaliable || 0;

  // 假分析：使用基于数据和时间戳的随机数
  const randomStatus = getSeededRandom(data, 'occupancy');
  let status = DetectionStatus.NORMAL;
  let score = 100;
  
  // 调整概率：35%警告，8%错误，57%正常
  if (randomStatus < 0.08) {
    status = DetectionStatus.ERROR;
    score = 40 + getSeededRandom(data, 'occupancy_error') * 20;
  } else if (randomStatus < 0.43) {
    status = DetectionStatus.WARNING;
    score = 60 + getSeededRandom(data, 'occupancy_warning') * 20;
  } else {
    score = 75 + getSeededRandom(data, 'occupancy_normal') * 25;
  }

  const indicators: DimensionAnalysisResult['indicators'] = [
    {
      name: '入住率',
      value: occupancyRate,
      expected: 70,
      deviation: occupancyRate - 70,
      status: status,
    },
    {
      name: '可用房间占比',
      value: roomTotal > 0 ? (roomAvaliable / roomTotal) * 100 : 0,
      expected: 30,
      deviation: roomTotal > 0 ? (roomAvaliable / roomTotal) * 100 - 30 : 0,
      status: status,
    },
  ];

  const messages = status !== DetectionStatus.NORMAL 
    ? [`入住率${occupancyRate.toFixed(1)}%，需要关注`] 
    : ['入住率数据正常'];

  return {
    dimension: DimensionType.OCCUPANCY,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status,
    score: Math.max(0, Math.min(100, score)),
    indicators,
    message: messages.join('；'),
    suggestions: status !== DetectionStatus.NORMAL ? ['建议优化价格策略'] : [],
    timestamp: new Date(),
  };
}

/**
 * 维度3：价格维度分析（Mock版本 - 假分析）
 */
export function analyzePriceDimension(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): DimensionAnalysisResult {
  // 从mock数据中取数
  const adr = data.adr || 0;
  const discountRate = data.discountRate || 0;
  const roomRevpar = data.roomRevpar || 0;

  // 假分析：使用基于数据和时间戳的随机数
  const randomStatus = getSeededRandom(data, 'price');
  let status = DetectionStatus.NORMAL;
  let score = 100;
  
  // 调整概率：28%警告，12%错误，60%正常
  if (randomStatus < 0.12) {
    status = DetectionStatus.ERROR;
    score = 40 + getSeededRandom(data, 'price_error') * 20;
  } else if (randomStatus < 0.4) {
    status = DetectionStatus.WARNING;
    score = 60 + getSeededRandom(data, 'price_warning') * 20;
  } else {
    score = 75 + getSeededRandom(data, 'price_normal') * 25;
  }

  const indicators: DimensionAnalysisResult['indicators'] = [
    {
      name: '平均房价(ADR)',
      value: adr,
      status: status,
    },
    {
      name: '折扣率',
      value: discountRate,
      expected: 30,
      deviation: discountRate - 30,
      status: status,
    },
    {
      name: '房间RevPAR',
      value: roomRevpar,
      status: status,
    },
  ];

  const messages = status !== DetectionStatus.NORMAL 
    ? ['价格数据存在异常'] 
    : ['价格数据正常'];

  return {
    dimension: DimensionType.PRICE,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status,
    score: Math.max(0, Math.min(100, score)),
    indicators,
    message: messages.join('；'),
    suggestions: status !== DetectionStatus.NORMAL ? ['建议优化价格策略'] : [],
    timestamp: new Date(),
  };
}

/**
 * 维度4：客源结构维度分析（Mock版本 - 假分析）
 */
export function analyzeCustomerSourceDimension(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): DimensionAnalysisResult {
  // 从mock数据中取数
  const fitRate = data.fitRoomNightRate || 0;
  const teamRate = data.teamRoomNightRate || 0;
  const memberRate = data.memberRoomNightRate || 0;
  const otaRate = data.otaRoomNightRate || 0;
  const storesRate = data.storesRoomNightRate || 0;

  // 假分析：使用基于数据和时间戳的随机数
  const randomStatus = getSeededRandom(data, 'customer_source');
  let status = DetectionStatus.NORMAL;
  let score = 100;
  
  // 调整概率：32%警告，10%错误，58%正常
  if (randomStatus < 0.1) {
    status = DetectionStatus.ERROR;
    score = 40 + getSeededRandom(data, 'customer_error') * 20;
  } else if (randomStatus < 0.42) {
    status = DetectionStatus.WARNING;
    score = 60 + getSeededRandom(data, 'customer_warning') * 20;
  } else {
    score = 75 + getSeededRandom(data, 'customer_normal') * 25;
  }

  const indicators: DimensionAnalysisResult['indicators'] = [
    { name: '散客房夜率', value: fitRate, status: status },
    { name: '团队房夜率', value: teamRate, status: status },
    { name: '会员房夜率', value: memberRate, status: status },
    { name: 'OTA房夜率', value: otaRate, status: status },
    { name: '门店房夜率', value: storesRate, status: status },
  ];

  const messages = status !== DetectionStatus.NORMAL 
    ? ['客源结构存在异常'] 
    : ['客源结构正常'];

  return {
    dimension: DimensionType.CUSTOMER_SOURCE,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status,
    score: Math.max(0, Math.min(100, score)),
    indicators,
    message: messages.join('；'),
    suggestions: status !== DetectionStatus.NORMAL ? ['建议优化客源结构'] : [],
    timestamp: new Date(),
  };
}

/**
 * 维度5：房态维度分析（Mock版本 - 假分析）
 */
export function analyzeRoomStatusDimension(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): DimensionAnalysisResult {
  // 从mock数据中取数
  const roomTotal = data.roomTotal || 0;
  const roomRepair = data.roomRepair || 0;
  const roomLock = data.roomLock || 0;
  const earlyCheckout = data.earlyCheckout || 0;
  const noshow = data.noshow || 0;
  const roomTake = data.roomTake || 0;

  // 假分析：使用基于数据和时间戳的随机数
  const randomStatus = getSeededRandom(data, 'room_status');
  let status = DetectionStatus.NORMAL;
  let score = 100;
  
  // 调整概率：25%警告，15%错误，60%正常
  if (randomStatus < 0.15) {
    status = DetectionStatus.ERROR;
    score = 40 + getSeededRandom(data, 'room_error') * 20;
  } else if (randomStatus < 0.4) {
    status = DetectionStatus.WARNING;
    score = 60 + getSeededRandom(data, 'room_warning') * 20;
  } else {
    score = 75 + getSeededRandom(data, 'room_normal') * 25;
  }

  const repairRatio = roomTotal > 0 ? (roomRepair / roomTotal) * 100 : 0;
  const lockRatio = roomTotal > 0 ? (roomLock / roomTotal) * 100 : 0;
  const earlyCheckoutRate = roomTake > 0 ? (earlyCheckout / roomTake) * 100 : 0;
  const noshowRate = roomTake > 0 ? (noshow / roomTake) * 100 : 0;

  const indicators: DimensionAnalysisResult['indicators'] = [
    {
      name: '维修房间占比',
      value: repairRatio,
      expected: 5,
      deviation: repairRatio - 5,
      status: status,
    },
    {
      name: '锁定房间占比',
      value: lockRatio,
      expected: 3,
      deviation: lockRatio - 3,
      status: status,
    },
    {
      name: '提前退房率',
      value: earlyCheckoutRate,
      expected: 3,
      deviation: earlyCheckoutRate - 3,
      status: status,
    },
    {
      name: '未到店率',
      value: noshowRate,
      expected: 2,
      deviation: noshowRate - 2,
      status: status,
    },
  ];

  const messages = status !== DetectionStatus.NORMAL 
    ? ['房态数据存在异常'] 
    : ['房态数据正常'];

  return {
    dimension: DimensionType.ROOM_STATUS,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status,
    score: Math.max(0, Math.min(100, score)),
    indicators,
    message: messages.join('；'),
    suggestions: status !== DetectionStatus.NORMAL ? ['建议检查房态情况'] : [],
    timestamp: new Date(),
  };
}

/**
 * 维度6：收入效率维度分析（Mock版本 - 假分析）
 */
export function analyzeRevenueEfficiencyDimension(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): DimensionAnalysisResult {
  // 从mock数据中取数
  const roomRevpar = data.roomRevpar || 0;
  const allRevpar = data.allRevpar || 0;
  const fitRoomNightRevenueAvg = data.fitRoomNightRevenueAvg || 0;
  const memberRoomNightRevenueAvg = data.memberRoomNightRevenueAvg || 0;

  // 假分析：使用基于数据和时间戳的随机数
  const randomStatus = getSeededRandom(data, 'revenue_efficiency');
  let status = DetectionStatus.NORMAL;
  let score = 100;
  
  // 调整概率：30%警告，8%错误，62%正常
  if (randomStatus < 0.08) {
    status = DetectionStatus.ERROR;
    score = 40 + getSeededRandom(data, 'efficiency_error') * 20;
  } else if (randomStatus < 0.38) {
    status = DetectionStatus.WARNING;
    score = 60 + getSeededRandom(data, 'efficiency_warning') * 20;
  } else {
    score = 75 + getSeededRandom(data, 'efficiency_normal') * 25;
  }

  const indicators: DimensionAnalysisResult['indicators'] = [
    { name: '房间RevPAR', value: roomRevpar, status: status },
    { name: '总RevPAR', value: allRevpar, status: status },
    { name: '散客房夜平均收入', value: fitRoomNightRevenueAvg, status: status },
    { name: '会员房夜平均收入', value: memberRoomNightRevenueAvg, status: status },
  ];

  const messages = status !== DetectionStatus.NORMAL 
    ? ['收入效率存在异常'] 
    : ['收入效率正常'];

  return {
    dimension: DimensionType.REVENUE_EFFICIENCY,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status,
    score: Math.max(0, Math.min(100, score)),
    indicators,
    message: messages.join('；'),
    suggestions: status !== DetectionStatus.NORMAL ? ['建议提高收入效率'] : [],
    timestamp: new Date(),
  };
}

/**
 * 维度7：时间维度分析（Mock版本 - 假分析）
 */
export function analyzeTimeDimension(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): DimensionAnalysisResult {
  // 从mock数据中取数
  const hourSum = data.hourSum || 0;
  const hourScale = data.hourScale || 0;

  // 假分析：使用基于数据和时间戳的随机数
  const randomStatus = getSeededRandom(data, 'time');
  let status = DetectionStatus.NORMAL;
  let score = 100;
  
  // 调整概率：22%警告，8%错误，70%正常
  if (randomStatus < 0.08) {
    status = DetectionStatus.ERROR;
    score = 40 + getSeededRandom(data, 'time_error') * 20;
  } else if (randomStatus < 0.3) {
    status = DetectionStatus.WARNING;
    score = 60 + getSeededRandom(data, 'time_warning') * 20;
  } else {
    score = 75 + getSeededRandom(data, 'time_normal') * 25;
  }

  const indicators: DimensionAnalysisResult['indicators'] = [
    { name: '小时总和', value: hourSum, status: status },
    { name: '小时比例', value: hourScale, status: status },
  ];

  const messages = status !== DetectionStatus.NORMAL 
    ? ['时间维度数据存在异常'] 
    : ['时间维度数据正常'];

  return {
    dimension: DimensionType.TIME,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status,
    score: Math.max(0, Math.min(100, score)),
    indicators,
    message: messages.join('；'),
    suggestions: status !== DetectionStatus.NORMAL ? ['建议关注时间趋势'] : [],
    timestamp: new Date(),
  };
}

/**
 * 维度8：综合健康度维度分析
 */
export function analyzeOverallHealthDimension(
  data: GroupBusinessReport,
  allDimensions: Partial<Record<DimensionType, DimensionAnalysisResult>>
): DimensionAnalysisResult {
  const indicators: DimensionAnalysisResult['indicators'] = [];
  let totalScore = 0;
  let dimensionCount = 0;
  let maxStatus = DetectionStatus.NORMAL;

  // 汇总所有维度的评分
  Object.values(allDimensions).forEach((dimResult) => {
    if (dimResult) {
      totalScore += dimResult.score;
      dimensionCount++;
      if (dimResult.status === DetectionStatus.CRITICAL) {
        maxStatus = DetectionStatus.CRITICAL;
      } else if (dimResult.status === DetectionStatus.ERROR && maxStatus !== DetectionStatus.CRITICAL) {
        maxStatus = DetectionStatus.ERROR;
      } else if (dimResult.status === DetectionStatus.WARNING && maxStatus === DetectionStatus.NORMAL) {
        maxStatus = DetectionStatus.WARNING;
      }
    }
  });

  const avgScore = dimensionCount > 0 ? totalScore / dimensionCount : 100;

  // 添加各维度评分指标
  Object.entries(allDimensions).forEach(([dim, result]) => {
    if (result) {
      indicators.push({
        name: `${getDimensionName(dim as DimensionType)}评分`,
        value: result.score,
        status: result.status,
      });
    }
  });

  const messages: string[] = [];
  if (avgScore < 60) {
    messages.push('综合健康度较低，建议全面检查');
  } else if (avgScore < 80) {
    messages.push('综合健康度良好，有改进空间');
  }

  return {
    dimension: DimensionType.OVERALL_HEALTH,
    storeId: data.hotelId,
    storeName: data.hotelName || '',
    status: maxStatus,
    score: Math.max(0, Math.min(100, avgScore)),
    indicators,
    message: messages.length > 0 ? messages.join('；') : '综合健康度良好',
    suggestions: generateOverallHealthSuggestions(allDimensions),
    timestamp: new Date(),
  };
}

/**
 * 执行8个维度的分析
 */
export function analyzeAllDimensions(
  data: GroupBusinessReport,
  historicalData?: GroupBusinessReport[]
): Record<DimensionType, DimensionAnalysisResult> {
  const results: Partial<Record<DimensionType, DimensionAnalysisResult>> = {};

  // 分析前7个维度
  results[DimensionType.REVENUE] = analyzeRevenueDimension(data, historicalData);
  results[DimensionType.OCCUPANCY] = analyzeOccupancyDimension(data, historicalData);
  results[DimensionType.PRICE] = analyzePriceDimension(data, historicalData);
  results[DimensionType.CUSTOMER_SOURCE] = analyzeCustomerSourceDimension(data, historicalData);
  results[DimensionType.ROOM_STATUS] = analyzeRoomStatusDimension(data, historicalData);
  results[DimensionType.REVENUE_EFFICIENCY] = analyzeRevenueEfficiencyDimension(data, historicalData);
  results[DimensionType.TIME] = analyzeTimeDimension(data, historicalData);

  // 综合健康度维度需要依赖其他维度
  results[DimensionType.OVERALL_HEALTH] = analyzeOverallHealthDimension(data, results);

  return results as Record<DimensionType, DimensionAnalysisResult>;
}

// ==================== 辅助函数 ====================

function generateOverallHealthSuggestions(
  allDimensions: Partial<Record<DimensionType, DimensionAnalysisResult>>
): string[] {
  const suggestions: string[] = [];
  const lowScoreDimensions = Object.entries(allDimensions)
    .filter(([_, result]) => result && result.score < 70)
    .map(([dim, _]) => getDimensionName(dim as DimensionType));

  if (lowScoreDimensions.length > 0) {
    suggestions.push(`建议优先关注以下维度：${lowScoreDimensions.join('、')}`);
  }
  return suggestions;
}

function getDimensionName(dimension: DimensionType): string {
  const names: Record<DimensionType, string> = {
    [DimensionType.REVENUE]: '营收',
    [DimensionType.OCCUPANCY]: '入住率',
    [DimensionType.PRICE]: '价格',
    [DimensionType.CUSTOMER_SOURCE]: '客源结构',
    [DimensionType.ROOM_STATUS]: '房态',
    [DimensionType.REVENUE_EFFICIENCY]: '收入效率',
    [DimensionType.TIME]: '时间',
    [DimensionType.OVERALL_HEALTH]: '综合健康度',
  };
  return names[dimension] || dimension;
}
